import React, { useEffect, useState } from 'react';
import { EditorState, ContentState, convertFromRaw, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './rich-text-editor.scss';
import ColumnDropdown from './ColumnDropdown';
import Modal from 'react-modal';
import { Icon } from 'fireflink-ui';
import { Tooltip } from '@material-ui/core';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { downloadImagesFromCloudFolder } from '@src/api/api_services';

const RichTextEditor = ({
  convertedContent,
  setConvertedContent,
  readOnly,
  toolbarHidden,
  toolbarOptions,
  mode,
  analyticsClasses = false,
  descriptionContentNotEditable,
  columns,
  steps,
}) => {
  const [editorState, setEditorState] = useState(null);
  const [focusState, setFocusState] = useState(false);
  const [column, setColumn] = useState(null);
  const [showIcon, setShownIcon] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [blobResponse, setBlobResponse] = useState(null);
  const [showImage, setShowImage] = useState(false);
  const defaultToolBarOptions = [
    'inline',
    'blockType',
    'fontSize',
    'fontFamily',
    'list',
    'textAlign',
    'colorPicker',
    'link',
    'emoji',
    'image',
    'remove',
    'history',
  ];
  useEffect(() => {
    let editorState = null;
    let cc = convertedContent ? JSON.parse(convertedContent) : {};
    if (convertedContent.length > 0) {
      if (cc) {
        editorState = EditorState.createWithContent(convertFromRaw(JSON.parse(convertedContent)));
      } else {
        if (mode === 'view') {
          const content = ContentState.createFromText('--');
          editorState = EditorState.createWithContent(content);
        } else {
          editorState = EditorState.createEmpty();
        }
      }
    }
    setEditorState(editorState);
  }, [convertedContent, mode]);

  const insertStepsIntoContent = (newSteps) => {
    const raw = JSON.parse(convertedContent);
    const blocks = raw.blocks;

    const stepsHeaderIndex = blocks.findIndex((b) => b.text === 'Steps to reproduce :');

    if (stepsHeaderIndex === -1) return convertedContent;

    let insertIndex = stepsHeaderIndex + 1;
    while (insertIndex < blocks.length && blocks[insertIndex].type === 'ordered-list-item') {
      insertIndex++;
    }

    const entityMap = raw.entityMap || {};
    const newStepBlocks = [];

    newSteps?.forEach((step, idx) => {
      const key = `autoStep-${Date.now()}-${idx}`;

      if (typeof step === 'string') {
        newStepBlocks.push({
          key,
          text: step,
          type: 'ordered-list-item',
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [],
          data: {},
        });
      } else if (step?.type === 'attachment' || Array.isArray(step)) {
        const attachments = Array.isArray(step) ? step : [step];

        attachments.forEach((img, i) => {
          const attachmentBlockKey = `${key}-attachment-${i}`;
          const entityKey = Object.keys(entityMap).length;
          const fileId = img?.id || img?.fileId;
          const imageName = img?.name?.split('*')[0] || img?.name;

          newStepBlocks.push({
            key: attachmentBlockKey,
            text: ' ',
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [{ offset: 0, length: 1, key: entityKey }],
            data: {},
          });

          entityMap[entityKey] = {
            type: 'IMAGE_ATTACHMENT',
            mutability: 'IMMUTABLE',
            data: { imageName, fileId },
          };
        });
      }
    });

    blocks.splice(insertIndex, 0, ...newStepBlocks);

    return JSON.stringify({
      ...raw,
      blocks,
    });
  };

  const openImagePreview = async (image) => {
    if (image?.fileId) {
      const { data } = await downloadImagesFromCloudFolder(image.fileId);
      setBlobResponse(data);
      setSelectedImage(image);
      setShowImage(true);
    }
  };

  const closeImagePreview = () => {
    setShowImage(false);
    setSelectedImage(null);
  };

  const downloadImage = async () => {
    try {
      const url = window.URL.createObjectURL(new Blob([blobResponse]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedImage?.name || 'image');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Image download failed:', err);
    }
  };

  useEffect(() => {
    if (column) {
      if (column?.value === 'Attachment') {
        const rowAttachments = (steps?.Attachment?.[0] || []).map((att) => ({
          ...att,
          type: 'attachment',
        }));
        setConvertedContent(insertStepsIntoContent(rowAttachments));
      } else {
        setConvertedContent(insertStepsIntoContent(steps[column?.value]));
      }
    }
  }, [column?.key]);

  const AtomicBlockRenderer = (props) => {
    const { block, contentState, blockProps } = props;
    const setEditorState = blockProps.setEditorState;
    const entity = contentState.getEntity(block.getEntityAt(0));
    const { imageName, fileId } = entity.getData();
    const image = { name: imageName, fileId };

    const isImage = /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(imageName);

    const handleDelete = () => {
      const content = props.contentState;
      const blockKey = block.getKey();

      const blockMap = content.getBlockMap().delete(blockKey);
      const newContentState = content.merge({
        blockMap,
      });

      const newEditorState = EditorState.push(
        props.editorState || EditorState.createWithContent(content),
        newContentState,
        'remove-range'
      );

      setEditorState(newEditorState);
    };

    return (
      <div className="flex items-center attachment-box">
        <Tooltip title={imageName}>
          <div
            onClick={() => {
              openImagePreview(image);
            }}
            className="custom-image-name image-name-width"
          >
            {imageName}
          </div>
        </Tooltip>
        {mode !== 'view' && (
          <Tooltip title="remove" placement="right">
            <div className="delete-icon-style-MTC-table ml-2">
              <CloseOutlinedIcon fontSize="small" className="cursor-pointer" onClick={handleDelete} />
            </div>
          </Tooltip>
        )}
      </div>
    );
  };

  const blockRendererFn = (block) => {
    const contentState = editorState?.getCurrentContent();
    const entityKey = block.getEntityAt(0);

    if (entityKey !== null && contentState) {
      const entity = contentState.getEntity(entityKey);
      if (entity && entity.getType() === 'IMAGE_ATTACHMENT') {
        return {
          component: AtomicBlockRenderer,
          editable: false,
          props: {
            setEditorState,
          },
        };
      }
    }

    const text = block.getText();
    if (block.getKey() === 'autoFillHint' || text.includes('{{autofill-icon}}')) {
      setShownIcon(true);
      return {
        component: () => (
          <div className="autofill-hint-block">
            <span>Click on</span>
            <Icon name="auto_fill_steps_icon" height={24} width={24} className="inline-icon" hoverFill />
            <span>Autofill the steps to Reproduce or</span>
            <span className="keyboard-chip">Alt + A</span>
          </div>
        ),
        editable: false,
      };
    }

    return null;
  };

  useEffect(() => {
    let editorState = null;
    if (convertedContent.length === 0) {
      if (mode === 'view') {
        const content = ContentState.createFromText('--');
        editorState = EditorState.createWithContent(content);
      } else {
        editorState = EditorState.createEmpty();
      }
      setEditorState(editorState);
    }
  }, [convertedContent]);

  const handleEditorChange = (state) => {
    setEditorState(state);
  };

  return (
    <>
      <div className="editor-wrapper">
        {editorState && (
          <Editor
            editorState={editorState}
            onEditorStateChange={handleEditorChange}
            wrapperClassName="wrapperClass"
            editorClassName={`heading-TextEditor-style ${analyticsClasses ? 'fontPoppinsRegularSm' : 'editorClass'}`}
            editorStyle={
              mode !== 'view'
                ? !focusState
                  ? { border: '1px solid #989797', borderRadius: '5px' }
                  : { border: '1px solid #1648c6', borderRadius: '5px' }
                : { border: 'none' }
            }
            readOnly={readOnly}
            onFocus={() => setFocusState(true)}
            onBlur={() => {
              setFocusState(false);
              let currentContentAsRaw = convertToRaw(editorState.getCurrentContent());
              setConvertedContent(JSON.stringify(currentContentAsRaw));
            }}
            toolbarHidden={toolbarHidden}
            toolbarClassName="toolbarClass"
            handlePastedText={() => false}
            toolbar={{
              options:
                Array.isArray(toolbarOptions) && toolbarOptions.length > 0 ? toolbarOptions : defaultToolBarOptions,
              fontFamily: {
                options: ['Arial', 'Georgia', 'Impact', 'OpenSans-Regular', 'Tahoma', 'Times New Roman', 'Verdana'],
              },
            }}
            blockRendererFn={blockRendererFn}
          />
        )}
        {mode !== 'view' && steps && (
          <div className="autofill-icon-wrapper">
            <ColumnDropdown
              columns={columns}
              onSelect={(value) => setColumn(value)}
              focusState={focusState}
              showIcon={showIcon}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={showImage}
        onRequestClose={closeImagePreview}
        className="modal-content-custom image-preview-modal"
        contentLabel="Image Preview Modal"
      >
        <div className="image-overlay-icons">
          <Tooltip title="Download">
            <button className="close-icon" onClick={downloadImage}>
              <FileDownloadOutlinedIcon fontSize="medium" />
            </button>
          </Tooltip>
          <Tooltip title="Cancel">
            <button onClick={closeImagePreview} className="close-icon ml-2">
              <CloseOutlinedIcon fontSize="medium" />
            </button>
          </Tooltip>
        </div>
        <div className="modal-content">
          <img
            src={blobResponse && URL.createObjectURL(blobResponse)}
            alt={selectedImage?.name}
            className="preview-image-style"
          />
        </div>
      </Modal>
    </>
  );
};

export default RichTextEditor;
